/**
 * LeaveApprovals — Wave 3.2 live-API wrapper around <ApprovalList>.
 *
 * Backend data shape for type='leave' (from seed_test_data.py):
 *   { leave_type, start_date, end_date, days, reason }
 *
 * P1.7 changes (2026-05-16):
 * - LeaveType select reads /v1/attendance/leave-types live. Hardcoded
 *   5-enum was fragile because admins can self-create LeaveTypes;
 *   anything they added didn't show up here.
 * - `days` is derived from start_date / end_date inclusively (end -
 *   start + 1). Override toggle reveals a manual input for half-days
 *   or unusual leave shapes.
 * - Top banner shows the selected worker's current leave_balance for
 *   each LeaveType so admins see how many days are available before
 *   they submit. Backed by /v1/attendance/leave-balances filtered
 *   client-side by worker_id.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ApprovalList, { ApprovalColumn, ApprovalFormField } from './ApprovalList';
import { attendanceApi } from '../../../api';
import { useTranslation } from 'react-i18next';

function pickStr(data: unknown, key: string): string {
  return typeof data === 'object' && data !== null && key in data
    ? String((data as Record<string, unknown>)[key] ?? '')
    : '';
}

function pickNum(data: unknown, key: string): number | null {
  if (typeof data !== 'object' || data === null || !(key in data)) return null;
  const v = (data as Record<string, unknown>)[key];
  return typeof v === 'number' ? v : null;
}

const LeaveApprovals: React.FC = () => {
  const { t } = useTranslation();

  const columns = useMemo<ApprovalColumn[]>(() => [
    {
      label: t('attendance:approvals.leave.colType'),
      render: (r) => pickStr(r.data, 'leave_type') || '—',
    },
    {
      label: t('attendance:approvals.leave.colPeriod'),
      render: (r) => (
        <Typography variant="caption">
          {pickStr(r.data, 'start_date') || '—'} →{' '}
          {pickStr(r.data, 'end_date') || '—'}
        </Typography>
      ),
    },
    {
      label: t('attendance:approvals.leave.colDays'),
      align: 'center',
      render: (r) => pickNum(r.data, 'days') ?? '—',
    },
    {
      label: t('attendance:approvals.leave.colReason'),
      render: (r) => (
        <Typography variant="caption" color="text.secondary">
          {pickStr(r.data, 'reason') || '—'}
        </Typography>
      ),
    },
  ], [t]);

  /** Does ANY active leave type permit half days?
   *
   *  `LeaveType.allow_half_day` is an admin policy that was read by nothing — this surface
   *  offered half-day options for every type, and `create_approval` now refuses the pairing
   *  with a clear message. Measured in production 2026-09-03: 2 leave types across all
   *  tenants, both `allow_half_day = false`, so today the options simply should not appear.
   *
   *  Coarser than the two worker clients on purpose. This dialog's field config is static and
   *  its `optionsLoader` runs once at mount with no access to the current form values, so it
   *  cannot narrow the list to the SELECTED type without reworking a shared dialog for an
   *  edge case (svdy-rules §16). "No type allows halves" is the case worth handling here; a
   *  wrong pairing when some type does is caught server-side, and the admin gets a sentence
   *  telling them which box to tick.
   */
  const [anyTypeAllowsHalfDay, setAnyTypeAllowsHalfDay] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const types = await attendanceApi.leaveTypesApi.list();
        if (!cancelled) {
          setAnyTypeAllowsHalfDay(
            types.some((tp) => (tp.status ?? 'active') === 'active' && tp.allow_half_day === true),
          );
        }
      } catch {
        // Degrade to hiding the half options rather than offering ones the server will
        // refuse: an unavailable choice is better than a rejected submission.
        if (!cancelled) setAnyTypeAllowsHalfDay(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const createFields = useMemo<ApprovalFormField[]>(() => [
    {
      key: 'leave_type',
      label: t('attendance:approvals.leave.formType'),
      type: 'dynamic_select',
      required: true,
      span: 12,
      optionsLoader: async () => {
        const types = await attendanceApi.leaveTypesApi.list();
        // Filter to active types only — admins commonly archive
        // legacy types instead of deleting them, and we don't want
        // those showing up as new-request options.
        return types
          .filter((tp) => (tp.status ?? 'active') === 'active')
          .map((tp) => ({ value: tp.name, label: tp.name }));
      },
    },
    {
      // Half-day. `leave_fraction_on` reads `duration` (and `duration_days`), so without
      // this an admin recording leave on someone's behalf could only ever book FULL days
      // while the worker portal and mobile could both book a half (2026-08-25).
      key: 'duration',
      label: t('attendance:approvals.leave.formDuration'),
      type: 'select',
      // Full width so the two DATES share the next row. At span 6 the duration select sat
      // beside the start date and pushed the end date onto a row of its own, so the reader
      // compared a start against nothing (owner report 2026-09-03). A span is a pair; the
      // fields that form it belong side by side.
      span: 12,
      // The SAME four branches the two worker clients offer, so an admin recording on behalf
      // sees the question the worker would have answered. `multi` is a UI-only branch: the wire
      // contract is unchanged and `create_approval` still receives duration ∈
      // full | half_am | half_pm, with the branch mapped in the submit path.
      options: [
        { value: 'multi', label: t('worker:leave.durationMulti') },
        { value: 'full', label: t('worker:leave.durationFull') },
        ...(anyTypeAllowsHalfDay ? [
          { value: 'half_am', label: t('worker:leave.durationHalfAm') },
          { value: 'half_pm', label: t('worker:leave.durationHalfPm') },
        ] : []),
      ],
      // The renderer feeds `placeholder` into MUI's helperText (ApprovalList line ~1760), so
      // this is where a select's explanation goes on this surface.
      placeholder: anyTypeAllowsHalfDay
        ? t('worker:leave.halfDayRule')
        : t('worker:leave.halfDayNotAllowedForAnyType'),
    },
    {
      key: 'start_date',
      label: t('attendance:approvals.leave.formStartDate'),
      type: 'date',
      required: true,
      span: 6,
    },
    {
      key: 'end_date',
      label: t('attendance:approvals.leave.formEndDate'),
      type: 'date',
      required: true,
      span: 6,
    },
    {
      // Counted by the SERVER, not by subtracting two dates in the browser.
      //
      // This field used to be `derived_days`, i.e. `(bT - aT)/86_400_000 + 1` —
      // CALENDAR days — and it was SUBMITTED, and `_extract_leave_payload` honoured
      // it, and `_apply_leave_balance_delta` deducted it. So a Friday-to-Monday leave
      // recorded here cost a worker FOUR days of entitlement while the same request
      // from the worker portal cost TWO (audit, 2026-08-25). Read-only now, and the
      // server recomputes on submit regardless of what is sent.
      key: 'days',
      label: t('attendance:approvals.leave.colDays'),
      type: 'derived_working_days',
      derivedFromDates: ['start_date', 'end_date'],
      halfDayFrom: 'duration',
      placeholder: t('attendance:approvals.helperWorkingDays'),
    },
    { key: 'reason', label: t('attendance:approvals.leave.colReason'), type: 'textarea', required: true },
  ], [t, anyTypeAllowsHalfDay]);

  /**
   * Render the worker's current leave-balance summary as a short
   * inline-stats line of chips. Shown above the form so admins can
   * spot 'this worker only has 0.5d annual left' before they type
   * 5 days into the dialog.
   */
  const loadLeaveBalanceBanner = useCallback(async (
    workerId: string,
  ): Promise<React.ReactNode | null> => {
    try {
      const [balances, types] = await Promise.all([
        attendanceApi.leaveBalancesApi.list(),
        attendanceApi.leaveTypesApi.list(),
      ]);
      const typeById = new Map(types.map((tp) => [tp.id, tp.name] as const));
      const my = balances.filter((b) => b.worker_id === workerId);
      if (my.length === 0) {
        return (
          <Typography variant="caption" color="text.secondary">
            {t('attendance:approvals.leave.noBalanceRows')}
          </Typography>
        );
      }
      return (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.5 }}
          >
            {t('attendance:approvals.leave.currentBalance')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {my.map((b) => {
              const typeName = typeById.get(b.leave_type_id) ?? '?';
              const remaining =
                Number(b.entitlement) +
                Number(b.carried_over ?? 0) -
                Number(b.used);
              const exhausted = remaining <= 0;
              return (
                <Chip
                  key={b.id}
                  size="small"
                  variant="outlined"
                  color={exhausted ? 'error' : remaining < 2 ? 'warning' : 'default'}
                  label={
                    <span>
                      <strong>{typeName}</strong>:{' '}
                      {remaining.toFixed(1)} / {Number(b.entitlement).toFixed(1)} d
                    </span>
                  }
                  sx={{
                    borderRadius: 1,
                    height: 22,
                    fontSize: '0.7rem',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      );
    } catch {
      return null;
    }
  }, [t]);

  return (
    <ApprovalList
      type="leave"
      title={t('attendance:approvals.leaveTitle')}
      description={t('attendance:approvals.leaveDescription')}
      recordTitle={t('attendance:approvals.leave.recordTitle')}
      filterLabel={t('attendance:approvals.filterLabel')}
      columns={columns}
      createFields={createFields}
      topBannerLoader={loadLeaveBalanceBanner}
    />
  );
};

export default LeaveApprovals;
